import React from 'react'
import { useCoachChat } from './CoachChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import CoachChatWaiting from './CoachChatWaiting'
import RecentChats from './RecentChats'
import IncomingCallCard from './IncomingCallCard'
import CoachChatSettings from './CoachChatSettings'
import VirtualWaitingRoom from './VirtualWaitingRoom'
import TextChat from './TextChat'
import VideoChat from './VideoChat'
import AudioChat from './AudioChat'
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import {
  getIdleColumnLeftClass,
  getIdleColumnMiddleClass,
  getIdleColumnRightClass,
  getSectionHeaderClass,
} from './coachDashboardStyles'

const CoachChatInner = () => {
  const {
    connectionState,
    setAvailability,
    initialAvailability,
    chatManager,
    ringToneOptions = [],
    messageSoundOptions = [],
    isDarkMode,
    getDarkModeClasses,
    stopRingtone,
  } = useCoachChat()
  const { t } = useTranslation('coach_chat')

  // Get current connection state from context (reactive)
  const { candidates, coaches, pendingChats, messages, activeChats } =
    connectionState

  // Apply dark mode class to .coach-chat-prototype container and body
  React.useEffect(() => {
    const prototypeContainer = document.querySelector('.coach-chat-prototype')
    const body = document.body

    if (prototypeContainer) {
      if (isDarkMode) {
        prototypeContainer.classList.add('dark-mode')
      } else {
        prototypeContainer.classList.remove('dark-mode')
      }
    }

    if (body && body.classList.contains('coach-layout')) {
      if (isDarkMode) {
        body.classList.add('dark-mode')
      } else {
        body.classList.remove('dark-mode')
      }
    }
  }, [isDarkMode])

  // Track current availability state - initialize with initialAvailability from context
  const [currentAvailability, setCurrentAvailability] = React.useState(
    initialAvailability || 'away',
  )

  // Track user's preference separately from effective room presence
  const [userAvailabilityPreference, setUserAvailabilityPreference] =
    React.useState(initialAvailability || 'away')

  // State for right pane collapse
  const [isRightPaneCollapsed, setIsRightPaneCollapsed] = React.useState(false)

  // Initialize and update current availability from connection state
  React.useEffect(() => {
    if (connectionState.availability) {
      setCurrentAvailability(connectionState.availability)
      setUserAvailabilityPreference(connectionState.availability)
    }
  }, [connectionState.availability])

  // Initialize availability from URL parameter only once on mount
  React.useEffect(() => {
    if (initialAvailability) {
      setCurrentAvailability(initialAvailability)
      setUserAvailabilityPreference(initialAvailability)
    }
  }, [initialAvailability])

  const buildIncomingCandidate = React.useCallback(
    (pendingChat) => {
      if (!pendingChat) return null

      const username = pendingChat.candidateToken || pendingChat.username
      const easyrtcid =
        pendingChat.candidateEasyrtcid ||
        pendingChat.candidateId ||
        pendingChat.id

      return {
        easyrtcid,
        displayName: chatManager.resolveCandidateDisplayName({
          username,
          easyrtcid,
          displayName: pendingChat.candidateName || pendingChat.displayName,
        }),
        username,
        chatMode: pendingChat.chatMode || 'text',
        browser: pendingChat.browser,
        isMobile: pendingChat.isMobile,
        locale: pendingChat.locale,
        chatSlot: pendingChat.chatSlot,
      }
    },
    [chatManager],
  )

  // Handle accepting a call
  const handleAcceptCall = React.useCallback(
    (candidate) => {
      // Stop ringtone immediately when accepting
      stopRingtone()
      // Accept the chat through the chat manager
      if (chatManager && candidate.easyrtcid) {
        try {
          const result = chatManager.acceptChat(candidate.easyrtcid)
          if (!result) {
            console.error('CoachChatInner - Failed to accept chat')
          }
        } catch (error) {
          console.error('CoachChatInner - Error accepting chat:', error)
        }
      } else {
        console.error(
          'CoachChatInner - Cannot accept chat: chatManager or candidate.easyrtcid not available',
        )
      }
    },
    [chatManager, stopRingtone],
  )

  // Handle rejecting a call
  const handleRejectCall = React.useCallback(
    (candidate) => {
      // Stop ringtone immediately when rejecting
      stopRingtone()
      // Decline the chat through the chat manager
      if (chatManager && candidate.easyrtcid) {
        try {
          chatManager.declineChat(candidate.easyrtcid)
        } catch (error) {
          console.error('CoachChatInner - Error declining chat:', error)
        }
      } else {
        console.error(
          'CoachChatInner - Cannot decline chat: chatManager or candidate.easyrtcid not available',
        )
      }
    },
    [chatManager, stopRingtone],
  )

  // Determine grid state based on active chats
  const gridState = React.useMemo(() => {
    const textChats = activeChats?.filter((c) => c.chatMode === 'text') || []
    const mediaChats =
      activeChats?.filter(
        (c) => c.chatMode === 'audio' || c.chatMode === 'video',
      ) || []

    if (mediaChats.length > 0) {
      return mediaChats[0].chatMode === 'video' ? 'video_chat' : 'audio_chat'
    }
    if (textChats.length > 0) {
      return 'text_chat'
    }
    return 'waiting'
  }, [activeChats])

  // Filter messages for a specific chat, merging live messages with per-slot history
  const getMessagesForChat = React.useCallback(
    (chatId, chatSlot, chatKey) => {
      const liveMessages = Array.isArray(messages) ? messages : []
      const slotKey = chatKey || (chatSlot != null ? `chat-${chatSlot}` : null)

      // chatId is unique per session. When both the query and the message have
      // it, use it as the sole discriminator so stale messages from a prior chat
      // on the same slot never bleed into the new chat.
      const matchesChat = (msg) => {
        if (chatId != null && msg.chatId != null) {
          return msg.chatId === chatId
        }
        // Slot-based fallback only when chatId is absent on one side
        if (slotKey && msg.chatKey === slotKey) return true
        if (chatSlot != null && msg.chatSlot === chatSlot) return true
        return false
      }

      const filtered = liveMessages.filter(matchesChat)
      const historyMessages =
        slotKey && connectionState.chats?.[slotKey]
          ? connectionState.chats[slotKey].chatHistory?.messages || []
          : []

      if (historyMessages.length === 0) {
        return filtered
      }

      const messageDedupeKey = (msg) =>
        `${msg.timestamp || ''}|${msg.content || msg.text || ''}`

      const merged = [...filtered]
      const seen = new Set(filtered.map(messageDedupeKey))

      historyMessages.forEach((msg) => {
        const dedupeKey = messageDedupeKey(msg)
        if (seen.has(dedupeKey)) return
        seen.add(dedupeKey)
        merged.push({
          ...msg,
          content: msg.text || msg.content,
          sender: msg.sender || 'candidate',
          timestamp: msg.timestamp || new Date().toISOString(),
        })
      })

      merged.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      )
      return merged
    },
    [messages, connectionState.chats],
  )

  // Get candidate object from active chat
  const getCandidateFromChat = React.useCallback(
    (chat) => {
      if (!chat) return null

      const candidateEasyrtcid =
        chat.candidateEasyrtcid || chat.candidateId || chat.easyrtcid

      if (!candidateEasyrtcid) return null

      // Try to get candidate from connectionState.candidates
      const candidateFromList = connectionState.candidates?.find(
        (c) => c.easyrtcid === candidateEasyrtcid,
      )

      if (candidateFromList) {
        return {
          ...candidateFromList,
          displayName:
            chatManager.resolveCandidateDisplayName(candidateFromList),
          chatMode: chat.chatMode || 'text',
          candidateToken: chat.candidateToken || candidateFromList.username, // Preserve candidateToken from chat
        }
      }

      // Fallback: construct candidate from chat data
      // Include candidateToken so it persists when candidate leaves
      return {
        easyrtcid: candidateEasyrtcid,
        displayName: chatManager.resolveCandidateDisplayName({
          username: chat.candidateToken || chat.username,
          easyrtcid: candidateEasyrtcid,
          displayName: chat.candidateName || chat.displayName,
          firstName: chat.firstName,
        }),
        username: chat.candidateToken || chat.username,
        candidateToken: chat.candidateToken, // Store candidateToken for caching
        chatMode: chat.chatMode || 'text',
      }
    },
    [connectionState.candidates, chatManager],
  )

  // Handle availability change - update user preference
  const handleAvailabilityChange = React.useCallback(
    (value) => {
      setUserAvailabilityPreference(value)
      setCurrentAvailability(value)
      setAvailability(value)
    },
    [setAvailability],
  )

  // Manage room presence from capacity / cool-down (single source of truth in manager)
  React.useEffect(() => {
    if (!chatManager || !chatManager.rtcService) return
    chatManager._updatePresenceForCapacity()
  }, [activeChats, userAvailabilityPreference, chatManager])

  // Get active chats organized by slot
  const chat1 = React.useMemo(() => {
    return activeChats?.find((chat) => chat.chatSlot === 1) || null
  }, [activeChats])

  const chat2 = React.useMemo(() => {
    return activeChats?.find((chat) => chat.chatSlot === 2) || null
  }, [activeChats])

  // Render pending chats directly in their assigned slots.
  const pendingChatSlot1 = React.useMemo(() => {
    return pendingChats?.find((chat) => (chat.chatSlot || 1) === 1) || null
  }, [pendingChats])

  const pendingChatSlot2 = React.useMemo(() => {
    return pendingChats?.find((chat) => chat.chatSlot === 2) || null
  }, [pendingChats])

  // Get grid configuration based on state
  const gridConfig = React.useMemo(() => {
    if (gridState === 'waiting' || gridState === 'text_chat') {
      return {
        columns: '1fr 1fr',
        areas: '"chat_1 chat_2"',
      }
    } else {
      // audio_chat or video_chat
      return {
        columns: '1fr',
        areas: '"chat_1"',
      }
    }
  }, [gridState])

  const isSlot1Empty = !chat1 && !pendingChatSlot1
  const showIdleThreeColumnLayout = isSlot1Empty && gridState === 'waiting'

  return (
    <div id="main" className="relative h-full w-full">
      <div
        className="chat-interface-grid-container grid h-full min-h-0 w-full"
        style={{
          gridTemplateColumns: showIdleThreeColumnLayout
            ? '1fr'
            : isRightPaneCollapsed
              ? '1fr 28px'
              : '1fr 400px',
          gridTemplateRows: '1fr',
          gridTemplateAreas: showIdleThreeColumnLayout
            ? '"main"'
            : '"main right_pane"',
        }}
      >
        <div
          className={`chat-interface-main h-full min-h-0 overflow-hidden ${showIdleThreeColumnLayout ? '' : 'p-4'} ${gridState}`}
          style={{ gridArea: 'main' }}
        >
          {showIdleThreeColumnLayout ? (
            <div
              className="grid h-full min-h-0 w-full"
              style={{ gridTemplateColumns: '1fr 1fr 1fr' }}
            >
              <div className={getIdleColumnLeftClass(getDarkModeClasses)}>
                <h2
                  className={`${getSectionHeaderClass(getDarkModeClasses)} mb-4 flex-shrink-0`}
                >
                  {t('coach_dashboard.your_chat_status', 'Your Chat Status')}
                </h2>
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                  <CoachChatSettings
                    embedded
                    currentAvailability={currentAvailability}
                    onAvailabilityChange={handleAvailabilityChange}
                    ringToneOptions={ringToneOptions}
                    messageSoundOptions={messageSoundOptions}
                    activeChats={activeChats}
                  />
                  <CoachChatWaiting embedded />
                </div>
              </div>

              <div className={getIdleColumnMiddleClass(getDarkModeClasses)}>
                <RecentChats embedded />
              </div>

              <div className={getIdleColumnRightClass(getDarkModeClasses)}>
                <VirtualWaitingRoom candidates={candidates} coaches={coaches} />
              </div>
            </div>
          ) : (
            <div
              className="grid h-full min-h-0 w-full gap-4"
              style={{
                gridTemplateColumns: gridConfig.columns,
                gridTemplateAreas: gridConfig.areas,
              }}
            >
              {/* Chat 1 Area */}
              <div
                className="h-full min-h-0 overflow-hidden"
                style={{ gridArea: 'chat_1' }}
              >
                {pendingChatSlot1 ? (
                  <IncomingCallCard
                    candidate={buildIncomingCandidate(pendingChatSlot1)}
                    onAcceptCall={handleAcceptCall}
                    onRejectCall={handleRejectCall}
                  />
                ) : chat1 ? (
                  (() => {
                    const candidate = getCandidateFromChat(chat1)
                    const chatMessages = getMessagesForChat(
                      chat1.id,
                      chat1.chatSlot,
                      chat1.chatKey || `chat-${chat1.chatSlot}`,
                    )

                    if (chat1.chatMode === 'video') {
                      return (
                        <div className="h-full w-full">
                          <VideoChat
                            candidate={candidate}
                            messages={chatMessages}
                            selectedCandidate={
                              connectionState.selectedCandidate
                            }
                          />
                        </div>
                      )
                    } else if (chat1.chatMode === 'audio') {
                      return (
                        <div className="h-full w-full">
                          <AudioChat
                            candidate={candidate}
                            messages={chatMessages}
                            selectedCandidate={
                              connectionState.selectedCandidate
                            }
                          />
                        </div>
                      )
                    } else {
                      return (
                        <TextChat
                          candidate={candidate}
                          messages={chatMessages}
                          selectedCandidate={connectionState.selectedCandidate}
                        />
                      )
                    }
                  })()
                ) : (
                  <CoachChatWaiting />
                )}
              </div>

              {/* Chat 2 Area - only shown for waiting/text_chat states */}
              {(gridState === 'waiting' || gridState === 'text_chat') && (
                <div
                  className="h-full min-h-0 overflow-hidden"
                  style={{ gridArea: 'chat_2' }}
                >
                  {pendingChatSlot2 ? (
                    <IncomingCallCard
                      candidate={buildIncomingCandidate(pendingChatSlot2)}
                      onAcceptCall={handleAcceptCall}
                      onRejectCall={handleRejectCall}
                    />
                  ) : chat2 ? (
                    (() => {
                      const candidate = getCandidateFromChat(chat2)
                      const chatMessages = getMessagesForChat(
                        chat2.id,
                        chat2.chatSlot,
                        chat2.chatKey || `chat-${chat2.chatSlot}`,
                      )

                      return (
                        <TextChat
                          candidate={candidate}
                          messages={chatMessages}
                          selectedCandidate={connectionState.selectedCandidate2}
                        />
                      )
                    })()
                  ) : (
                    <RecentChats />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Right Pane — Virtual Waiting Room when a chat is active or incoming */}
        {!showIdleThreeColumnLayout && (
          <div
            className={`flex h-full min-h-0 overflow-hidden border-l ${getDarkModeClasses(
              'border-gray-200 bg-gray-50',
              'border-gray-600 bg-gray-900',
            )}`}
            style={{
              gridArea: 'right_pane',
            }}
          >
            {/* Toggle Column */}
            <div
              className={`flex h-full w-7 items-center justify-center ${
                !isRightPaneCollapsed ? 'border-r' : ''
              } ${getDarkModeClasses(
                'border-gray-200 bg-gray-50',
                'border-gray-600 bg-gray-900',
              )} cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800`}
              onClick={() => setIsRightPaneCollapsed(!isRightPaneCollapsed)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsRightPaneCollapsed(!isRightPaneCollapsed)
                }
              }}
              aria-label={
                isRightPaneCollapsed
                  ? 'Expand right pane'
                  : 'Collapse right pane'
              }
            >
              {isRightPaneCollapsed ? (
                <ChevronLeftIcon
                  className={`h-5 w-5 ${getDarkModeClasses(
                    'text-gray-600',
                    'text-gray-400',
                  )}`}
                />
              ) : (
                <ChevronRightIcon
                  className={`h-5 w-5 ${getDarkModeClasses(
                    'text-gray-600',
                    'text-gray-400',
                  )}`}
                />
              )}
            </div>

            {/* Content Column */}
            {!isRightPaneCollapsed && (
              <div className="min-h-0 flex-1 overflow-hidden">
                <VirtualWaitingRoom candidates={candidates} coaches={coaches} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CoachChatInner
